import { useState } from 'react';
import { 
  FaDownload, 
  FaBell, 
  FaBolt, 
  FaMicrophone, 
  FaCheck, 
  FaEllipsisH, 
  FaPlay,
  FaPause,
  FaTrash,
  FaBook,
  FaClipboardCheck,
  FaCalendarAlt,
  FaLock,
  FaExclamationCircle
} from 'react-icons/fa';

const MobileFeatures = () => {
  const [activeTab, setActiveTab] = useState('offline');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [selectedNote, setSelectedNote] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Sample data for demonstration
  const offlineContent = [
    { 
      id: 1, 
      title: 'Introduction to React Hooks', 
      type: 'lesson',
      course: 'Advanced React Development',
      size: '24 MB',
      downloadStatus: 'downloaded',
      lastAccessed: '2023-11-09T14:30:00'
    },
    { 
      id: 2, 
      title: 'State Management with Redux', 
      type: 'lesson',
      course: 'Advanced React Development',
      size: '18 MB',
      downloadStatus: 'downloaded',
      lastAccessed: '2023-11-08T10:15:00'
    },
    { 
      id: 3, 
      title: 'React Performance Optimization', 
      type: 'lesson',
      course: 'Advanced React Development',
      size: '32 MB',
      downloadStatus: 'downloading',
      progress: 65
    },
    { 
      id: 4, 
      title: 'Week 3 Assignment: Shopping Cart', 
      type: 'assignment',
      course: 'Advanced React Development',
      size: '5 MB',
      downloadStatus: 'not_downloaded'
    },
    { 
      id: 5, 
      title: 'Database Design Fundamentals', 
      type: 'lesson',
      course: 'Database Systems',
      size: '42 MB',
      downloadStatus: 'downloaded',
      lastAccessed: '2023-11-07T16:45:00'
    },
  ];
  
  const notifications = [
    {
      id: 1,
      title: 'Assignment Due Tomorrow',
      message: 'Your "Data Structures Implementation" assignment is due tomorrow at 11:59 PM.',
      time: '2023-11-10T09:00:00',
      type: 'deadline',
      read: false,
      course: 'Data Structures & Algorithms'
    },
    {
      id: 2,
      title: 'New Course Material Available',
      message: 'Week 4 materials for "Advanced React Development" are now available.',
      time: '2023-11-09T15:30:00',
      type: 'content',
      read: true,
      course: 'Advanced React Development'
    },
    {
      id: 3,
      title: 'Quiz Score: 92%',
      message: 'You scored 92% on the "JavaScript Fundamentals" quiz. Great job!',
      time: '2023-11-09T11:45:00',
      type: 'achievement',
      read: true,
      course: 'JavaScript Masterclass'
    },
    {
      id: 4,
      title: 'Live Session Reminder',
      message: 'Your scheduled live session on "Database Optimization" starts in 1 hour.',
      time: '2023-11-10T13:00:00',
      type: 'reminder',
      read: false,
      course: 'Database Systems'
    },
    {
      id: 5,
      title: 'Discussion Reply',
      message: 'Maria Garcia replied to your question in the React Hooks discussion forum.',
      time: '2023-11-08T16:20:00',
      type: 'discussion',
      read: true,
      course: 'Advanced React Development'
    },
  ];
  
  const quickActions = [
    {
      id: 1,
      title: 'Continue: React Hooks',
      subtitle: '65% complete',
      progress: 65,
      icon: <FaBook className="text-blue-500" />,
      type: 'course',
      lastAccessed: '2023-11-09T14:30:00'
    },
    {
      id: 2,
      title: 'Due: Shopping Cart Assignment',
      subtitle: 'Due in 2 days',
      icon: <FaClipboardCheck className="text-red-500" />,
      type: 'assignment',
      dueDate: '2023-11-12T23:59:00'
    },
    {
      id: 3,
      title: 'Upcoming: Database Live Session',
      subtitle: 'Today at 2:00 PM',
      icon: <FaCalendarAlt className="text-purple-500" />,
      type: 'event',
      startTime: '2023-11-10T14:00:00'
    },
    {
      id: 4,
      title: 'Review: JavaScript Quiz',
      subtitle: 'Score: 92%',
      icon: <FaCheck className="text-green-500" />,
      type: 'review',
      completedDate: '2023-11-09T11:45:00'
    },
  ];
  
  const voiceNotes = [
    {
      id: 1,
      title: 'React Hooks Questions',
      duration: 45, // seconds
      date: '2023-11-09T15:30:00',
      course: 'Advanced React Development',
      transcription: 'I have a question about useEffect cleanup functions. When exactly do they run and how can I test them properly?'
    },
    {
      id: 2,
      title: 'Database Design Ideas',
      duration: 78, // seconds
      date: '2023-11-08T11:20:00',
      course: 'Database Systems',
      transcription: 'For the e-commerce project, I think we should use a NoSQL database because of the flexible schema requirements. Need to consider how to structure the product catalog.'
    },
    {
      id: 3,
      title: 'Assignment Clarification',
      duration: 32, // seconds
      date: '2023-11-07T09:45:00',
      course: 'Advanced React Development',
      transcription: 'For the shopping cart assignment, do we need to implement the checkout process or just the cart functionality?'
    },
  ];
  
  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
  };
  
  // Format time remaining for due dates
  const formatTimeRemaining = (dateString) => {
    const dueDate = new Date(dateString);
    const now = new Date();
    const diffMs = dueDate - now;
    
    if (diffMs < 0) {
      return 'Overdue';
    }
    
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} left`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} left`;
    } else {
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} left`;
    }
  };
  
  // Format seconds to MM:SS
  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 px-6 py-4 border-b">
        <h2 className="text-lg font-medium">Mobile Learning Features</h2>
        <p className="text-sm text-gray-500 mt-1">Access your learning materials anytime, anywhere</p>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex -mb-px">
          <button
            onClick={() => setActiveTab('offline')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'offline' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaDownload className="inline mr-2" />
            Offline Learning
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'notifications' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaBell className="inline mr-2" />
            Push Notifications
          </button>
          <button
            onClick={() => setActiveTab('quickactions')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'quickactions' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaBolt className="inline mr-2" />
            Quick Actions
          </button>
          <button
            onClick={() => setActiveTab('voicenotes')}
            className={`py-4 px-6 text-sm font-medium ${activeTab === 'voicenotes' 
              ? 'border-b-2 border-primary text-primary' 
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaMicrophone className="inline mr-2" />
            Voice Notes
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Offline Learning */}
        {activeTab === 'offline' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-medium">Downloaded Content</h3>
              <div className="flex items-center">
                <span className="text-sm text-gray-500 mr-2">
                  {offlineContent.filter(c => c.downloadStatus === 'downloaded').length} items
                </span>
                <button className="text-primary text-sm hover:underline">
                  Manage Storage
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              {offlineContent.map(content => (
                <div 
                  key={content.id} 
                  className={`border rounded-lg overflow-hidden ${content.downloadStatus === 'downloaded' ? 'bg-white' : 'bg-gray-50'}`}
                >
                  <div className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="text-md font-medium">{content.title}</h4>
                        <div className="flex items-center mt-1 text-sm text-gray-500">
                          <span>{content.course}</span>
                          <span className="mx-2">•</span>
                          <span>{content.type === 'lesson' ? 'Lesson' : 'Assignment'}</span>
                          <span className="mx-2">•</span>
                          <span>{content.size}</span>
                        </div>
                      </div>
                      <div className="flex items-center">
                        {content.downloadStatus === 'downloaded' && (
                          <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded flex items-center mr-2">
                            <FaCheck className="mr-1" /> Downloaded
                          </span>
                        )}
                        <button className="text-gray-400 hover:text-gray-500">
                          <FaEllipsisH />
                        </button>
                      </div>
                    </div>
                    
                    {content.downloadStatus === 'downloading' && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>Downloading...</span>
                          <span>{content.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full" 
                            style={{ width: `${content.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                    
                    {content.downloadStatus === 'downloaded' && (
                      <div className="mt-3 flex justify-between items-center">
                        <span className="text-xs text-gray-500">
                          Last accessed: {formatDate(content.lastAccessed)}
                        </span>
                        <button className="bg-primary text-white rounded-md px-3 py-1 text-xs font-medium hover:bg-primary-dark transition-colors">
                          Open
                        </button>
                      </div>
                    )}
                    
                    {content.downloadStatus === 'not_downloaded' && (
                      <div className="mt-3 flex justify-end">
                        <button className="bg-gray-200 text-gray-800 rounded-md px-3 py-1 text-xs font-medium hover:bg-gray-300 transition-colors flex items-center">
                          <FaDownload className="mr-1" /> Download
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-800 mb-2">Offline Access Settings</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FaWifi className="text-blue-500 mr-2" />
                    <span className="text-sm text-blue-700">Auto-download on Wi-Fi only</span>
                  </div>
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input type="checkbox" name="toggle" id="wifi" className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" />
                    <label htmlFor="wifi" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FaLock className="text-blue-500 mr-2" />
                    <span className="text-sm text-blue-700">Require authentication for offline content</span>
                  </div>
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input type="checkbox" name="toggle" id="auth" className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" checked />
                    <label htmlFor="auth" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FaExclamationCircle className="text-blue-500 mr-2" />
                    <span className="text-sm text-blue-700">Warn before using mobile data</span>
                  </div>
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input type="checkbox" name="toggle" id="warn" className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" checked />
                    <label htmlFor="warn" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Push Notifications */}
        {activeTab === 'notifications' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-medium">Notification Center</h3>
              <div className="flex items-center">
                <span className="text-sm text-gray-500 mr-2">
                  {notifications.filter(n => !n.read).length} unread
                </span>
                <button className="text-primary text-sm hover:underline">
                  Mark all as read
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              {notifications.map(notification => (
                <div 
                  key={notification.id} 
                  className={`border rounded-lg overflow-hidden ${!notification.read ? 'border-l-4 border-l-primary' : ''}`}
                >
                  <div className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex items-start">
                        <div className={`p-2 rounded-full mr-3 ${getNotificationTypeStyles(notification.type).bgColor}`}>
                          {getNotificationTypeStyles(notification.type).icon}
                        </div>
                        <div>
                          <h4 className="text-md font-medium">{notification.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                          <div className="flex items-center mt-2 text-xs text-gray-500">
                            <span>{notification.course}</span>
                            <span className="mx-2">•</span>
                            <span>{formatDate(notification.time)}</span>
                          </div>
                        </div>
                      </div>
                      <button className="text-gray-400 hover:text-gray-500">
                        <FaEllipsisH />
                      </button>
                    </div>
                    
                    {notification.type === 'deadline' && (
                      <div className="mt-3 flex justify-end">
                        <button className="bg-primary text-white rounded-md px-3 py-1 text-xs font-medium hover:bg-primary-dark transition-colors mr-2">
                          View Assignment
                        </button>
                        <button className="bg-gray-200 text-gray-800 rounded-md px-3 py-1 text-xs font-medium hover:bg-gray-300 transition-colors">
                          Snooze
                        </button>
                      </div>
                    )}
                    
                    {notification.type === 'reminder' && (
                      <div className="mt-3 flex justify-end">
                        <button className="bg-primary text-white rounded-md px-3 py-1 text-xs font-medium hover:bg-primary-dark transition-colors mr-2">
                          Join Session
                        </button>
                        <button className="bg-gray-200 text-gray-800 rounded-md px-3 py-1 text-xs font-medium hover:bg-gray-300 transition-colors">
                          Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-800 mb-3">Notification Preferences</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Assignment Deadlines</span>
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input type="checkbox" name="toggle" id="deadlines" className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" checked />
                    <label htmlFor="deadlines" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">New Course Content</span>
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input type="checkbox" name="toggle" id="content" className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" checked />
                    <label htmlFor="content" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Discussion Replies</span>
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input type="checkbox" name="toggle" id="replies" className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" checked />
                    <label htmlFor="replies" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Grade Updates</span>
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input type="checkbox" name="toggle" id="grades" className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" checked />
                    <label htmlFor="grades" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Live Session Reminders</span>
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input type="checkbox" name="toggle" id="sessions" className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" checked />
                    <label htmlFor="sessions" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        {activeTab === 'quickactions' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-medium">Quick Access Widget</h3>
              <button className="text-primary text-sm hover:underline">
                Customize
              </button>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {quickActions.map(action => (
                  <div key={action.id} className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center mb-3">
                      <div className="p-2 rounded-full bg-gray-100 mr-3">
                        {action.icon}
                      </div>
                      <div className="truncate">
                        <h4 className="text-sm font-medium truncate">{action.title}</h4>
                        <p className="text-xs text-gray-500 truncate">{action.subtitle}</p>
                      </div>
                    </div>
                    
                    {action.type === 'course' && action.progress && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div 
                            className="bg-blue-500 h-1.5 rounded-full" 
                            style={{ width: `${action.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                    
                    {action.type === 'assignment' && action.dueDate && (
                      <div className="mt-2">
                        <span className="text-xs text-red-600">
                          {formatTimeRemaining(action.dueDate)}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Recent Activity</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="p-2 rounded-full bg-blue-100 mr-3">
                      <FaBook className="text-blue-500" />
                    </div>
                    <div>
                      <h5 className="text-sm font-medium">Completed: React Hooks Lesson 3</h5>
                      <p className="text-xs text-gray-500">Advanced React Development</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">2 hours ago</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="p-2 rounded-full bg-green-100 mr-3">
                      <FaCheck className="text-green-500" />
                    </div>
                    <div>
                      <h5 className="text-sm font-medium">Submitted: JavaScript Quiz</h5>
                      <p className="text-xs text-gray-500">JavaScript Masterclass</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">Yesterday</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="p-2 rounded-full bg-purple-100 mr-3">
                      <FaDownload className="text-purple-500" />
                    </div>
                    <div>
                      <h5 className="text-sm font-medium">Downloaded: Database Design Fundamentals</h5>
                      <p className="text-xs text-gray-500">Database Systems</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">3 days ago</span>
                </div>
              </div>
            </div>
            
            <div className="bg-primary bg-opacity-10 rounded-lg p-4">
              <h4 className="text-sm font-medium text-primary mb-2">Widget Settings</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Show on home screen</span>
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input type="checkbox" name="toggle" id="homescreen" className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" checked />
                    <label htmlFor="homescreen" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Auto-refresh content</span>
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input type="checkbox" name="toggle" id="refresh" className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" checked />
                    <label htmlFor="refresh" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Prioritize deadlines</span>
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input type="checkbox" name="toggle" id="deadlines-priority" className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" checked />
                    <label htmlFor="deadlines-priority" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Voice Notes */}
        {activeTab === 'voicenotes' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-medium">Voice Notes & Feedback</h3>
              <button 
                className={`${isRecording 
                  ? 'bg-red-500 hover:bg-red-600' 
                  : 'bg-primary hover:bg-primary-dark'} text-white rounded-md px-4 py-2 text-sm font-medium transition-colors flex items-center`}
                onClick={() => {
                  if (isRecording) {
                    setIsRecording(false);
                    setRecordingTime(0);
                  } else {
                    setIsRecording(true);
                    // In a real app, this would start recording
                  }
                }}
              >
                <FaMicrophone className="mr-2" />
                {isRecording ? `Recording (${formatDuration(recordingTime)})` : 'Record New Note'}
              </button>
            </div>
            
            {isRecording && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center justify-between">
                <div className="flex items-center">
                  <div className="animate-pulse h-3 w-3 bg-red-500 rounded-full mr-2"></div>
                  <span className="text-red-700 font-medium">Recording in progress...</span>
                </div>
                <div className="flex space-x-2">
                  <button className="bg-white text-gray-700 rounded-md px-3 py-1 text-sm font-medium border border-gray-300 hover:bg-gray-50 transition-colors">
                    Pause
                  </button>
                  <button 
                    className="bg-red-500 text-white rounded-md px-3 py-1 text-sm font-medium hover:bg-red-600 transition-colors"
                    onClick={() => setIsRecording(false)}
                  >
                    Stop
                  </button>
                </div>
              </div>
            )}
            
            <div className="space-y-4 mb-6">
              {voiceNotes.map(note => (
                <div 
                  key={note.id} 
                  className={`border rounded-lg overflow-hidden ${selectedNote === note.id ? 'border-primary' : ''}`}
                  onClick={() => setSelectedNote(selectedNote === note.id ? null : note.id)}
                >
                  <div className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex items-start">
                        <div className="p-2 rounded-full bg-purple-100 mr-3">
                          <FaMicrophone className="text-purple-500" />
                        </div>
                        <div>
                          <h4 className="text-md font-medium">{note.title}</h4>
                          <div className="flex items-center mt-1 text-xs text-gray-500">
                            <span>{formatDuration(note.duration)}</span>
                            <span className="mx-2">•</span>
                            <span>{formatDate(note.date)}</span>
                            <span className="mx-2">•</span>
                            <span>{note.course}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button 
                          className={`p-2 rounded-full ${isPlaying && selectedNote === note.id ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700'} hover:bg-gray-200 transition-colors`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isPlaying && selectedNote === note.id) {
                              setIsPlaying(false);
                            } else {
                              setSelectedNote(note.id);
                              setIsPlaying(true);
                            }
                          }}
                        >
                          {isPlaying && selectedNote === note.id ? <FaPause /> : <FaPlay />}
                        </button>
                        <button 
                          className="p-2 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FaEllipsisH />
                        </button>
                      </div>
                    </div>
                    
                    {selectedNote === note.id && (
                      <div className="mt-4">
                        <div className="bg-gray-100 rounded-lg p-3 text-sm text-gray-700">
                          <p>{note.transcription}</p>
                        </div>
                        
                        <div className="mt-3 flex justify-between">
                          <div className="flex space-x-2">
                            <button className="bg-gray-200 text-gray-800 rounded-md px-3 py-1 text-xs font-medium hover:bg-gray-300 transition-colors flex items-center">
                              <FaDownload className="mr-1" /> Export
                            </button>
                            <button className="bg-gray-200 text-gray-800 rounded-md px-3 py-1 text-xs font-medium hover:bg-gray-300 transition-colors flex items-center">
                              Share
                            </button>
                          </div>
                          <button className="text-red-500 text-xs hover:underline flex items-center">
                            <FaTrash className="mr-1" /> Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-800 mb-3">Voice Note Settings</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Auto-transcribe notes</span>
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input type="checkbox" name="toggle" id="transcribe" className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" checked />
                    <label htmlFor="transcribe" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">High-quality audio</span>
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input type="checkbox" name="toggle" id="quality" className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" />
                    <label htmlFor="quality" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Auto-sync to cloud</span>
                  <div className="relative inline-block w-10 mr-2 align-middle select-none">
                    <input type="checkbox" name="toggle" id="sync" className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" checked />
                    <label htmlFor="sync" className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function for notification type styles
const getNotificationTypeStyles = (type) => {
  switch (type) {
    case 'deadline':
      return {
        icon: <FaCalendarAlt className="text-red-500" />,
        bgColor: 'bg-red-100'
      };
    case 'content':
      return {
        icon: <FaBook className="text-blue-500" />,
        bgColor: 'bg-blue-100'
      };
    case 'achievement':
      return {
        icon: <FaTrophy className="text-yellow-500" />,
        bgColor: 'bg-yellow-100'
      };
    case 'reminder':
      return {
        icon: <FaBell className="text-purple-500" />,
        bgColor: 'bg-purple-100'
      };
    case 'discussion':
      return {
        icon: <FaComments className="text-green-500" />,
        bgColor: 'bg-green-100'
      };
    default:
      return {
        icon: <FaBell className="text-gray-500" />,
        bgColor: 'bg-gray-100'
      };
  }
};

// Missing FaWifi icon
const FaWifi = ({ className }) => (
  <svg 
    className={className} 
    fill="currentColor" 
    viewBox="0 0 640 512" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M634.91 154.88C457.74-8.99 182.19-8.93 5.09 154.88c-6.66 6.16-6.79 16.59-.35 22.98l34.24 33.97c6.14 6.1 16.02 6.23 22.4.38 145.92-133.68 371.3-133.71 517.25 0 6.38 5.85 16.26 5.71 22.4-.38l34.24-33.97c6.43-6.39 6.3-16.82-.36-22.98zM320 352c-35.35 0-64 28.65-64 64s28.65 64 64 64 64-28.65 64-64-28.65-64-64-64zm202.67-83.59c-115.26-101.93-290.21-101.82-405.34 0-6.9 6.1-7.12 16.69-.57 23.15l34.44 33.99c6 5.92 15.66 6.32 22.05.8 83.95-72.57 209.74-72.41 293.49 0 6.39 5.52 16.05 5.13 22.05-.8l34.44-33.99c6.56-6.46 6.33-17.06-.56-23.15z"></path>
  </svg>
);

// Missing FaComments icon
const FaComments = ({ className }) => (
  <svg 
    className={className} 
    fill="currentColor" 
    viewBox="0 0 576 512" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M416 192c0-88.4-93.1-160-208-160S0 103.6 0 192c0 34.3 14.1 65.9 38 92-13.4 30.2-35.5 54.2-35.8 54.5-2.2 2.3-2.8 5.7-1.5 8.7S4.8 352 8 352c36.6 0 66.9-12.3 88.7-25 32.2 15.7 70.3 25 111.3 25 114.9 0 208-71.6 208-160zm122 220c23.9-26 38-57.7 38-92 0-66.9-53.5-124.2-129.3-148.1.9 6.6 1.3 13.3 1.3 20.1 0 105.9-107.7 192-240 192-10.8 0-21.3-.8-31.7-1.9C207.8 439.6 281.8 480 368 480c41 0 79.1-9.2 111.3-25 21.8 12.7 52.1 25 88.7 25 3.2 0 6.1-1.9 7.3-4.8 1.3-2.9.7-6.3-1.5-8.7-.3-.3-22.4-24.2-35.8-54.5z"></path>
  </svg>
);

// Missing FaTrophy icon
const FaTrophy = ({ className }) => (
  <svg 
    className={className} 
    fill="currentColor" 
    viewBox="0 0 576 512" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M552 64H448V24c0-13.3-10.7-24-24-24H152c-13.3 0-24 10.7-24 24v40H24C10.7 64 0 74.7 0 88v56c0 35.7 22.5 72.4 61.9 100.7 31.5 22.7 69.8 37.1 110 41.7C203.3 338.5 240 360 240 360v72h-48c-35.3 0-64 20.7-64 56v12c0 6.6 5.4 12 12 12h296c6.6 0 12-5.4 12-12v-12c0-35.3-28.7-56-64-56h-48v-72s36.7-21.5 68.1-73.6c40.3-4.6 78.6-19 110-41.7 39.3-28.3 61.9-65 61.9-100.7V88c0-13.3-10.7-24-24-24zM99.3 192.8C74.9 175.2 64 155.6 64 144v-16h64.2c1 32.6 5.8 61.2 12.8 86.2-15.1-5.2-29.2-12.4-41.7-21.4zM512 144c0 16.1-17.7 36.1-35.3 48.8-12.5 9-26.7 16.2-41.8 21.4 7-25 11.8-53.6 12.8-86.2H512v16z"></path>
  </svg>
);

export default MobileFeatures;